import React, { useEffect, useState } from "react";
import axios from "../axios";
import "./IncomeTaxFilingStatus.css";

const API_URL = process.env.REACT_APP_API_URL;

const IncomeTaxFilingStatus = () => {
  const [records, setRecords] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Enhanced filter states
  const [filterMode, setFilterMode] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, filed, not_filed
  const [paymentFilter, setPaymentFilter] = useState("all"); // all, received, pending
  const [tdsFilter, setTdsFilter] = useState("all"); // all, received, pending

  const [selectedFY, setSelectedFY] = useState("");
  const [formData, setFormData] = useState({
    credential: "",
    staff: "",
    itr_form_number: "",
    fees_charged: "",
    filed_on: "",
    received_on: "",
    tds_refund_amount: "",
    tds_received: false,
    remarks: "",
  });

  // Load all credentials
  const fetchCredentials = async () => {
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.get(`${API_URL}api/income-tax/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setCredentials(res.data);
    } catch (error) {
      console.error("Error fetching credentials:", error);
    }
  };

  // Load staff options
  const fetchStaffOptions = async () => {
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.get(`${API_URL}api/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setStaffOptions(res.data);
    } catch (error) {
      console.error("Error fetching staff options:", error);
      setStaffOptions([]);
    }
  };

  const fetchFY = async () => {
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.get(`${API_URL}api/income-tax-fy/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFinancialYears(res.data);
      if (res.data.length > 0) setSelectedFY(res.data[0].id);
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("access_token");
    try {
      const res = await axios.get(`${API_URL}api/income-tax-filing/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setRecords(res.data);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFY();
    fetchCredentials();
    fetchStaffOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const parseNumberOrNull = (value) => {
    if (!value || value === "") return null;
    const parsed = Number(value);
    return isNaN(parsed) ? null : parsed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("access_token");

    const payload = {
      credential: parseNumberOrNull(formData.credential),
      financial_year: parseNumberOrNull(selectedFY),
      staff: parseNumberOrNull(formData.staff),
      itr_form_number: formData.itr_form_number || null,
      fees_charged: parseNumberOrNull(formData.fees_charged),
      filed_on: formatDateForAPI(formData.filed_on),
      received_on: formatDateForAPI(formData.received_on),
      tds_refund_amount: parseNumberOrNull(formData.tds_refund_amount),
      tds_received: Boolean(formData.tds_received),
      remarks: formData.remarks || "",
    };

    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === "") {
        delete payload[key];
      }
    });

    payload.credential = parseNumberOrNull(formData.credential);
    payload.financial_year = parseNumberOrNull(selectedFY);

    console.log("Payload being sent:", payload);

    try {
      if (editingId) {
        await axios.put(
          `${API_URL}api/income-tax-filing/${editingId}/`,
          payload,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        await axios.post(`${API_URL}api/income-tax-filing/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setEditingId(null);
      setIsFormVisible(false);
      setFormData({
        credential: "",
        staff: "",
        itr_form_number: "",
        fees_charged: "",
        filed_on: "",
        received_on: "",
        tds_refund_amount: "",
        tds_received: false,
        remarks: "",
      });
      fetchData();
    } catch (err) {
      console.error("Submission error:", err.response?.data || err.message);
      alert("Error: " + (err.response?.data?.detail || JSON.stringify(err.response?.data) || err.message));
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("access_token");

    try {
      await axios.delete(`${API_URL}api/income-tax-filing/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (error) {
      console.error("Error deleting record:", error.response?.data || error.message);
      alert("Failed to delete the record.");
    }
  };

  const handleEdit = (record) => {
    setFormData({
      credential: record.credential,
      staff: record.staff || "",
      itr_form_number: record.itr_form_number || "",
      fees_charged: record.fees_charged || "",
      filed_on: record.filed_on || "",
      received_on: record.received_on || "",
      tds_refund_amount: record.tds_refund_amount || "",
      tds_received: record.tds_received || false,
      remarks: record.remarks || "",
    });
    setSelectedFY(record.financial_year);
    setEditingId(record.id);
    setIsFormVisible(true);
  };

  const autoCreateMissingRecords = async () => {
    if (!selectedFY) {
      alert("⚠️ Please select a financial year first.");
      return;
    }

    const token = localStorage.getItem("access_token");

    const existingCombinations = new Set(
      records
        .filter((r) => String(r.financial_year) === String(selectedFY))
        .map((r) => `${r.credential}-${r.financial_year}`)
    );

    const payloadsToCreate = [];

    for (const cred of credentials) {
      const comboKey = `${cred.id}-${selectedFY}`;
      if (!existingCombinations.has(comboKey)) {
        payloadsToCreate.push({
          credential: cred.id,
          financial_year: Number(selectedFY),
          tds_received: false,
          remarks: "",
        });
      }
    }

    if (payloadsToCreate.length === 0) {
      alert("✅ All credentials have filing records for this year.");
      return;
    }

    try {
      for (const payload of payloadsToCreate) {
        await axios.post(`${API_URL}api/income-tax-filing/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      alert(`✅ Created ${payloadsToCreate.length} records for FY ${selectedFY}.`);
      fetchData();
    } catch (err) {
      console.error("Auto-create error:", err.response?.data || err.message);
      alert("❌ Error while auto-creating records.");
    }
  };

  // Calculate financial statistics
  const calculateStats = (filteredRecords) => {
    const stats = {
      totalRecords: filteredRecords.length,
      filedCount: 0,
      notFiledCount: 0,
      
      // Fees Statistics
      totalFeesCharged: 0,
      totalFeesReceived: 0,
      totalFeesPending: 0,
      feesReceivedCount: 0,
      feesPendingCount: 0,
      
      // TDS Statistics
      totalTDSRefund: 0,
      totalTDSReceived: 0,
      totalTDSPending: 0,
      tdsReceivedCount: 0,
      tdsPendingCount: 0,
    };

    filteredRecords.forEach(rec => {
      // Filing status
      if (rec.filed_on) {
        stats.filedCount++;
      } else {
        stats.notFiledCount++;
      }

      // Fees
      const feesCharged = parseFloat(rec.fees_charged) || 0;
      stats.totalFeesCharged += feesCharged;

      if (rec.tds_received && feesCharged > 0) {
        stats.totalFeesReceived += feesCharged;
        stats.feesReceivedCount++;
      } else if (feesCharged > 0) {
        stats.totalFeesPending += feesCharged;
        stats.feesPendingCount++;
      }

      // TDS
      const tdsAmount = parseFloat(rec.tds_refund_amount) || 0;
      stats.totalTDSRefund += tdsAmount;

      if (rec.received_on && tdsAmount > 0) {
        stats.totalTDSReceived += tdsAmount;
        stats.tdsReceivedCount++;
      } else if (tdsAmount > 0) {
        stats.totalTDSPending += tdsAmount;
        stats.tdsPendingCount++;
      }
    });

    return stats;
  };

  // Apply all filters
  const getFilteredRecords = () => {
    return records.filter(rec => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        (rec.credential_display || "").toLowerCase().includes(searchLower) ||
        (rec.itr_form_number || "").toLowerCase().includes(searchLower) ||
        (rec.remarks || "").toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      // Status filter
      if (statusFilter === "filed" && !rec.filed_on) return false;
      if (statusFilter === "not_filed" && rec.filed_on) return false;

      // Payment filter
      if (paymentFilter === "received" && (!rec.tds_received || !rec.fees_charged)) return false;
      if (paymentFilter === "pending" && (rec.tds_received || !rec.fees_charged)) return false;

      // TDS filter
      if (tdsFilter === "received" && (!rec.received_on || !rec.tds_refund_amount)) return false;
      if (tdsFilter === "pending" && (rec.received_on || !rec.tds_refund_amount)) return false;

      return true;
    });
  };

  const filteredRecords = getFilteredRecords();
  const stats = calculateStats(filteredRecords);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Quick filter presets
  const applyQuickFilter = (preset) => {
    switch(preset) {
      case 'fees_pending':
        setStatusFilter('all');
        setPaymentFilter('pending');
        setTdsFilter('all');
        break;
      case 'tds_pending':
        setStatusFilter('all');
        setPaymentFilter('all');
        setTdsFilter('pending');
        break;
      case 'not_filed':
        setStatusFilter('not_filed');
        setPaymentFilter('all');
        setTdsFilter('all');
        break;
      case 'all_pending':
        setStatusFilter('all');
        setPaymentFilter('pending');
        setTdsFilter('pending');
        break;
      default:
        setStatusFilter('all');
        setPaymentFilter('all');
        setTdsFilter('all');
    }
  };

  return (
    <div className="filing-status-container">
      <div className="filing-header">
        <h2>💼 Income Tax Filing Status</h2>
        
        <div className="header-controls">
          <div className="form-group">
            <label>Financial Year</label>
            <select
              value={selectedFY}
              onChange={(e) => setSelectedFY(e.target.value)}
              required
            >
              {financialYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.financial_year}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setIsFormVisible(!isFormVisible)}
            className="add-btn"
          >
            {isFormVisible ? "❌ Cancel" : "➕ Add New Record"}
          </button>

          <button onClick={autoCreateMissingRecords} className="auto-create-btn">
            ⚙️ Auto-Create Missing
          </button>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="financial-summary">
        <div className="summary-section">
          <h3>💰 Fees Summary</h3>
          <div className="summary-cards">
            <div className="summary-card total">
              <div className="card-icon">💵</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalFeesCharged)}</div>
                <div className="card-label">Total Fees Charged</div>
                <div className="card-count">{stats.totalRecords} records</div>
              </div>
            </div>

            <div className="summary-card success">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalFeesReceived)}</div>
                <div className="card-label">Fees Received</div>
                <div className="card-count">{stats.feesReceivedCount} payments</div>
              </div>
            </div>

            <div className="summary-card warning">
              <div className="card-icon">⏳</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalFeesPending)}</div>
                <div className="card-label">Fees Pending</div>
                <div className="card-count">{stats.feesPendingCount} pending</div>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <h3>🏦 TDS Refund Summary</h3>
          <div className="summary-cards">
            <div className="summary-card total">
              <div className="card-icon">💸</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalTDSRefund)}</div>
                <div className="card-label">Total TDS Refund</div>
                <div className="card-count">{stats.tdsReceivedCount + stats.tdsPendingCount} refunds</div>
              </div>
            </div>

            <div className="summary-card success">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalTDSReceived)}</div>
                <div className="card-label">TDS Received</div>
                <div className="card-count">{stats.tdsReceivedCount} received</div>
              </div>
            </div>

            <div className="summary-card danger">
              <div className="card-icon">⚠️</div>
              <div className="card-content">
                <div className="card-value">{formatCurrency(stats.totalTDSPending)}</div>
                <div className="card-label">TDS Pending</div>
                <div className="card-count">{stats.tdsPendingCount} pending</div>
              </div>
            </div>
          </div>
        </div>

        <div className="summary-section">
          <h3>📊 Filing Status</h3>
          <div className="summary-cards">
            <div className="summary-card info">
              <div className="card-icon">📋</div>
              <div className="card-content">
                <div className="card-value">{stats.filedCount}</div>
                <div className="card-label">Filed Returns</div>
                <div className="card-percentage">
                  {stats.totalRecords > 0 ? Math.round((stats.filedCount / stats.totalRecords) * 100) : 0}% complete
                </div>
              </div>
            </div>

            <div className="summary-card warning">
              <div className="card-icon">📝</div>
              <div className="card-content">
                <div className="card-value">{stats.notFiledCount}</div>
                <div className="card-label">Not Filed</div>
                <div className="card-percentage">
                  {stats.totalRecords > 0 ? Math.round((stats.notFiledCount / stats.totalRecords) * 100) : 0}% pending
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Filter Buttons */}
      <div className="quick-filters">
        <button 
          className={`quick-filter-btn ${statusFilter === 'all' && paymentFilter === 'all' && tdsFilter === 'all' ? 'active' : ''}`}
          onClick={() => applyQuickFilter('all')}
        >
          🔍 All Records ({records.length})
        </button>
        <button 
          className={`quick-filter-btn ${statusFilter === 'not_filed' ? 'active' : ''}`}
          onClick={() => applyQuickFilter('not_filed')}
        >
          📝 Not Filed ({stats.notFiledCount})
        </button>
        <button 
          className={`quick-filter-btn ${paymentFilter === 'pending' ? 'active' : ''}`}
          onClick={() => applyQuickFilter('fees_pending')}
        >
          💰 Fees Pending ({stats.feesPendingCount})
        </button>
        <button 
          className={`quick-filter-btn ${tdsFilter === 'pending' ? 'active' : ''}`}
          onClick={() => applyQuickFilter('tds_pending')}
        >
          🏦 TDS Pending ({stats.tdsPendingCount})
        </button>
      </div>

      {isFormVisible && (
        <div className="form-section">
          <h3>{editingId ? "✏️ Edit Record" : "➕ Add New Record"}</h3>
          
          {/* Financial Year Selector */}
          <div className="form-group">
            <label>Select Financial Year</label>
            <select
              value={selectedFY}
              onChange={(e) => setSelectedFY(e.target.value)}
              required
            >
              {financialYears.map((fy) => (
                <option key={fy.id} value={fy.id}>
                  {fy.financial_year}
                </option>
              ))}
            </select>
          </div>

          {/* Credential Selector */}
          <div className="form-group">
            <label>Select Credential (Surname - UserID)</label>
            <select
              name="credential"
              value={formData.credential}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              {credentials
                .filter(
                  (cred) =>
                    editingId ||
                    !records.some(
                      (record) =>
                        record.credential === cred.id &&
                        record.financial_year === Number(selectedFY)
                    )
                )
                .map((cred) => (
                  <option key={cred.id} value={cred.id}>
                    {cred.surname} ({cred.user_id})
                  </option>
                ))}
            </select>
          </div>

          {/* Filing Form */}
          <form className="filing-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* Staff Field */}
              <div className="form-group">
                <label>Staff</label>
                <select name="staff" value={formData.staff} onChange={handleChange}>
                  <option value="">-- Select Staff --</option>
                  {staffOptions.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.username || staff.first_name || staff.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* ITR Form Number */}
              <div className="form-group">
                <label>ITR Form Number</label>
                <input
                  type="text"
                  name="itr_form_number"
                  value={formData.itr_form_number}
                  onChange={handleChange}
                  placeholder="e.g., ITR-1, ITR-2"
                />
              </div>

              {/* Fees Charged */}
              <div className="form-group">
                <label>💰 Fees Charged</label>
                <input
                  type="number"
                  step="0.01"
                  name="fees_charged"
                  value={formData.fees_charged}
                  onChange={handleChange}
                  placeholder="Enter amount"
                />
              </div>

              {/* Filed On */}
              <div className="form-group">
                <label>📅 Filed On</label>
                <input
                  type="date"
                  name="filed_on"
                  value={formData.filed_on ? new Date(formData.filed_on).toISOString().split("T")[0] : ""}
                  onChange={handleChange}
                />
              </div>

              {/* TDS Refund Amount */}
              <div className="form-group">
                <label>🏦 TDS Refund Amount</label>
                <input
                  type="number"
                  step="0.01"
                  name="tds_refund_amount"
                  value={formData.tds_refund_amount}
                  onChange={handleChange}
                  placeholder="Expected refund amount"
                />
              </div>

              {/* Received On (TDS) */}
              <div className="form-group">
                <label>📆 TDS Received On</label>
                <input
                  type="date"
                  name="received_on"
                  value={formData.received_on ? new Date(formData.received_on).toISOString().split("T")[0] : ""}
                  onChange={handleChange}
                />
              </div>

              {/* Remarks */}
              <div className="form-group full-width">
                <label>📝 Remarks</label>
                <textarea
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Add any notes or comments..."
                />
              </div>
            </div>

            {/* PROMINENT PAYMENT STATUS SECTION */}
            <div className="payment-status-section">
              <h4>💵 Payment Status</h4>
              <div className="payment-toggle-container">
                <div 
                  className={`payment-toggle ${formData.tds_received ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, tds_received: !formData.tds_received})}
                >
                  <div className="toggle-slider">
                    <div className="toggle-button"></div>
                  </div>
                  <div className="toggle-label">
                    {formData.tds_received ? (
                      <span className="status-received">✅ Payment Received</span>
                    ) : (
                      <span className="status-pending">⏳ Payment Pending</span>
                    )}
                  </div>
                </div>
                <div className="payment-help-text">
                  Click to toggle between Received and Pending
                </div>
              </div>
            </div>

            <button type="submit" className="submit-btn">
              {editingId ? "💾 Update Record" : "➕ Create Record"}
            </button>
          </form>
        </div>
      )}

      {/* Advanced Filters */}
      <div className="advanced-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>🔍 Search</label>
            <input
              type="text"
              placeholder="Search by User ID, Form Number, Remarks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <label>📋 Filing Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="filed">Filed</option>
              <option value="not_filed">Not Filed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>💰 Payment Status</label>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="received">Received</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div className="filter-group">
            <label>🏦 TDS Status</label>
            <select value={tdsFilter} onChange={(e) => setTdsFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="received">Received</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        <div className="filter-results">
          Showing <strong>{filteredRecords.length}</strong> of <strong>{records.length}</strong> records
        </div>
      </div>

      {/* Records Table */}
      <div className="table-wrapper">
        <table className="records-table">
          <thead>
            <tr>
              <th className="sticky-col">Actions</th>
              <th>User ID</th>
              <th>Year</th>
              <th>Form</th>
              <th>Fees</th>
              <th>Payment</th>
              <th>Filed On</th>
              <th>TDS Refund</th>
              <th>TDS Status</th>
              <th>Received On</th>
              <th>Staff</th>
              <th>Remarks</th>
            </tr>
          </thead>

          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="12" className="no-data">
                  No records found matching your filters
                </td>
              </tr>
            ) : (
              filteredRecords.map((rec) => {
                const feesCharged = parseFloat(rec.fees_charged) || 0;
                const tdsAmount = parseFloat(rec.tds_refund_amount) || 0;
                const feesReceived = rec.tds_received && feesCharged > 0;
                const feesPending = !rec.tds_received && feesCharged > 0;
                const tdsReceived = rec.received_on && tdsAmount > 0;
                const tdsPending = !rec.received_on && tdsAmount > 0;

                return (
                  <tr key={rec.id} className={!rec.filed_on ? 'row-not-filed' : ''}>
                    <td className="actions-cell sticky-col">
                      <div className="action-buttons">
                        <button 
                          className="btn-icon edit-btn" 
                          onClick={() => handleEdit(rec)}
                          title="Edit Record"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon delete-btn"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this record?')) {
                              handleDelete(rec.id);
                            }
                          }}
                          title="Delete Record"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                    <td className="user-id-cell">
                      <strong>{rec.credential_display || rec.credential}</strong>
                    </td>
                    <td>{rec.financial_year_display || rec.financial_year}</td>
                    <td>{rec.itr_form_number || "-"}</td>
                    <td className="amount-cell">
                      {feesCharged > 0 ? formatCurrency(feesCharged) : "-"}
                    </td>
                    <td>
                      {feesCharged > 0 ? (
                        <span className={`status-badge ${feesReceived ? 'success' : 'warning'}`}>
                          {feesReceived ? '✅ Received' : '⏳ Pending'}
                        </span>
                      ) : (
                        <span className="status-badge neutral">-</span>
                      )}
                    </td>
                    <td>
                      {rec.filed_on ? (
                        <span className="date-badge success">
                          {new Date(rec.filed_on).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="status-badge warning">Not Filed</span>
                      )}
                    </td>
                    <td className="amount-cell">
                      {tdsAmount > 0 ? formatCurrency(tdsAmount) : "-"}
                    </td>
                    <td>
                      {tdsAmount > 0 ? (
                        <span className={`status-badge ${tdsReceived ? 'success' : 'danger'}`}>
                          {tdsReceived ? '✅ Received' : '⚠️ Pending'}
                        </span>
                      ) : (
                        <span className="status-badge neutral">-</span>
                      )}
                    </td>
                    <td>
                      {rec.received_on ? (
                        <span className="date-badge">
                          {new Date(rec.received_on).toLocaleDateString()}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{rec.staff_display || "-"}</td>
                    <td className="remarks-cell">{rec.remarks || "-"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncomeTaxFilingStatus;